/**
 * Quiz component that displays a series of questions and manages the quiz logic.
 * 
 * This component uses a custom hook `useQuizLogic` to manage the quiz state, including:
 * - `currentQuestion` (number): The index of the current question being displayed.
 * - `selectedAnswers` (array): The answers selected by the user for each question.
 * - `answeredQuestions` (array): A list of question IDs that have been answered.
 * - `handleAnswerOptionClick` (function): Callback function to handle the selection of an answer option.
 * 
 * The Quiz component renders a list of Question components, passing necessary props to each one.
 * 
 * Props passed to Question components:
 * - `data` (object): The question data, including question text and answer options, with an added `id`.
 * - `isCurrentQuestion` (boolean): Indicates if the question is the current active question.
 * - `handleAnswerOptionClick` (function): Function to handle the answer option click event.
 * - `selectedAnswer` (string): The currently selected answer for the question.
 * - `setSelectedAnswer` (function): Function to set the selected answer.
 * - `answeredQuestions` (array): List of answered question IDs.
 * 
 * CSS:
 * - The component uses styles from `QuestionStyles.css`.
 * 
 * @component
 * @example
 * return (
 *   <Quiz />
 * )
 */
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom'; // ADDED
import '../components/features/Quiz/QuestionStyles.css'; // Adjusted path for QuestionStyles.css
import Question from '../components/features/Quiz/Question';
import questions from '../components/features/Quiz/questions.json'; // Import questions
import useQuizLogic from '../components/features/Quiz/QuizLogic'; // Import the custom hook
import useHeaderVisibility from '../hooks/useHeaderVisibility'; // Import custom hook for header visibility
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';

function Quiz() {
  const {
    currentQuestion,
    selectedAnswers,
    handleAnswerOptionClick,
    handleSelectAnswer,
    resetQuiz,
    page,
    goToNextPage,
    goToPreviousPage,
    questionsPerPage,
    developerMode,
    toggleDeveloperMode,
  } = useQuizLogic();

  const totalQuestions = developerMode ? 4 : questions.length;
  const answeredCount = selectedAnswers.filter(
    (answer) => answer !== undefined
  ).length;
  const progress = (answeredCount / totalQuestions) * 100;

  const startIndex = page * questionsPerPage;
  const currentQuestions = questions.slice(
    startIndex,
    startIndex + questionsPerPage
  );

  // Use the custom hook to track header visibility
  const isHeaderVisible = useHeaderVisibility();
  const isProgressFixed = !isHeaderVisible; // Fix the progress bar when the header is hidden

  const headerVisible = useHeaderVisibility();
  const [visibleQuestions, setVisibleQuestions] = useState({});
  const questionRefs = useRef({});
  const observerRef = useRef(null);

  // Simplified answer handler: no tracking for scroll
  const handleAnswerOptionClickEnhanced = (dimension, option, questionId) => {
    handleAnswerOptionClick(dimension, option, questionId);
  };

  // Setup intersection observer to detect visible questions
  useEffect(() => {
    // Create new IntersectionObserver
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          const id = entry.target.id.replace('question-', '');
          setVisibleQuestions(prev => ({
            ...prev,
            [id]: entry.isIntersecting
          }));
        });
      },
      {
        rootMargin: '-100px 0px',
        threshold: 0.3
      }
    );
    
    // Observe all question elements
    Object.values(questionRefs.current).forEach(ref => {
      if (ref && observerRef.current) {
        observerRef.current.observe(ref);
      }
    });
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);
  
  // Handle pagination with smooth scrolling
  const handlePaginationClick = (pageChange) => {
    if (pageChange > 0) {
      goToNextPage();
      // Scroll to top after page content updates
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    } else if (pageChange < 0) {
      goToPreviousPage();
    }
  };

  // Calculate whole number progress percentage
  const calculateProgressPercentage = () => {
    const answeredCount = selectedAnswers.filter(answer => answer !== undefined).length;
    return Math.floor((answeredCount / questions.length) * 100);
  };
  
  const progressPercentage = calculateProgressPercentage();

  const displayedQuestionNumber = currentQuestion + 1; // NEW: real active question number
  const totalDisplayedQuestions = totalQuestions;      // respects developer mode

  return (
    <div className="quiz-page quiz-aesthetic-root">
      {/* Progress Bar */}
      <div className={`progress-bar-container ${!headerVisible ? 'fixed' : ''}`}>
        <div className="progress-bar-content">
          <div className="progress-stats">
            <span className="progress-label">
              Question {displayedQuestionNumber} of {totalDisplayedQuestions}
            </span>
            <span className="progress-percentage">{progressPercentage}% Complete</span>
          </div>
          <div className="progress-bar">
            <div 
              className="progress" 
              style={{ width: `${progressPercentage}%` }}
            ></div>
          </div>
        </div>
      </div>
      
      {/* NEW: Cohesive hero-style header */}
      <header className="mbti-quiz-header">
        <h1 className="mbti-quiz-title">
          Personality Alignment <span className="mbti-gradient">Assessment</span>
        </h1>
        <p className="mbti-quiz-sub">
          Evidence‑informed cognitive preference profiling to illuminate specialty fit and development focus.
        </p>
        <div className="mbti-cta-cluster">
          <Link to="/quiz-choice" className="mbti-cta-secondary">Other Quizzes</Link>
          <Link to="/home" className="mbti-cta-ghost">Home</Link>
          {developerMode && (
            <button className="mbti-cta-primary" onClick={toggleDeveloperMode}>
              Dev Mode: ON
            </button>
          )}
        </div>
      </header>

      <div className="app">
        <div className="quiz-container">
          {/* Inline utility buttons (retain) */}
          <button className="refresh-button" onClick={resetQuiz}>
            Reset quiz answers
          </button>
          <button
            className="developer-mode-button"
            onClick={toggleDeveloperMode}
          >
            {developerMode ? 'Disable Developer Mode' : 'Enable Developer Mode'}
          </button>

          {/* Questions */}
            <div className="questions">
              {currentQuestions.map((question, index) => (
                <Question
                  key={index + startIndex}
                  data={{ ...question, id: index + startIndex }}
                  isCurrentQuestion={currentQuestion === index + startIndex}
                  handleAnswerOptionClick={(dimension, option, id) =>
                    handleAnswerOptionClickEnhanced(dimension, option, id)
                  }
                  selectedAnswer={selectedAnswers[index + startIndex]}
                  setSelectedAnswer={(answer) =>
                    handleSelectAnswer(answer, index + startIndex)
                  }
                  isVisible={visibleQuestions[question.id]}
                  ref={el => questionRefs.current[question.id] = el}
                />
              ))}
            </div>

          {/* Pagination */}
          <div className="pagination">
            {page > 0 && (
              <button onClick={() => handlePaginationClick(-1)}>
                <FaArrowLeft className="icon-left" /> Previous
              </button>
            )}
            {(page + 1) * questionsPerPage < totalQuestions && (
              <button onClick={() => handlePaginationClick(1)}>
                Next <FaArrowRight className="icon-right" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Dev indicator (kept) */}
      {developerMode && (
        <button className="developer-mode-button" onClick={toggleDeveloperMode}>
          Developer Mode: {developerMode ? 'ON' : 'OFF'}
        </button>
      )}
    </div>
  );
}


export default Quiz;
